import { proposalrequest } from '@mep/pae-commons';
import i18n from 'i18next';
import moment from 'moment';
import businessDays from 'moment-business-days';

import BooleanPreview from '@/components/BooleanPreview';
import { renderProposalRequestActions } from '@/modules/proposalRequest/components/ProposalRequestRowActions';
import { renderProposalRequestStatus } from '@/modules/proposalRequest/components/ProposalRequestStatus';
import { renderProposalRequestType } from '@/modules/proposalRequest/components/ProposalRequestType';
import { renderProposalUserAssignedCell } from '@/modules/proposalRequest/components/ProposalUserAssigned';
import { ROUTE_PATH_PROPOSAL_REQUEST } from '@/modules/proposalRequest/contants/proposalRequest';
import { useRenderName } from '@/modules/proposalRequest/hooks/useSocialDenominationType';
import { createRenderLinkRow } from '@/utils/createRenderLinkRow';
import { renderMunicipality, renderProvince } from '@/utils/location';
import { showNomenclature, showPrice } from '@/utils/utils';

const { VALIDATED, ACCEPTED } = proposalrequest;

const renderBusinessDays = (value, defaultValue) => {
  if (!value && !defaultValue)
    return '-';
  const diff = Math.abs(businessDays(value || defaultValue).businessDiff(moment(new Date()), 'days'));
  if (!diff)
    return moment(value || defaultValue).fromNow();
  return showNomenclature(diff, i18n.t('actors:process.nomenclature'));
};

export const commonColumns = [
  {
    key: 'socialDenomination',
    sorteable: true,
    title: 'proposalRequest.columns.name',
    dataIndex: 'socialDenomination',
    ellipsis: true,
    render: createRenderLinkRow({
      path: ROUTE_PATH_PROPOSAL_REQUEST,
      info: useRenderName,
      subKeysToShow: [
        'owner',
        'subjectType',
        'miPymeExpectedWorkers',
        'approved',
        'endorsement',
        'createdAt',
      ],
    }),
  },
  {
    key: 'subjectType',
    title: 'proposalRequest.columns.type',
    ellipsis: true,
    dataIndex: 'subjectType',
    sorteable: true,
    responsive: ['lg'],
    render: renderProposalRequestType
  },
  {
    key: 'status',
    title: 'status',
    align: 'center',
    dataIndex: 'status',
    sorteable: true,
    render: renderProposalRequestStatus,
    responsive: ['lg']
  },
  {
    key: 'province',
    title: 'province',
    dataIndex: ['address', 'province'],
    responsive: ['lg'],
    align: 'center',
    ellipsis: true,
    width: 150,
    render: renderProvince
  },
  {
    key: 'municipality',
    title: 'municipality',
    dataIndex: ['address', 'municipality'],
    responsive: ['lg'],
    align: 'center',
    width: 150,
    ellipsis: true,
    render: renderMunicipality
  },
  {
    key: 'createdAt',
    title: 'createdAt',
    type: 'date',
    sorteable: true,
    align: 'center',
    dataIndex: 'createdAt',
    width: 150,
    responsive: ['lg']
  },
];

export const columns = [
  {
    key: 'socialDenomination',
    sorteable: true,
    title: 'proposalRequest.columns.name',
    dataIndex: 'socialDenomination',
    ellipsis: true,
    render: createRenderLinkRow({
      path: ROUTE_PATH_PROPOSAL_REQUEST,
      info: useRenderName,
      subKeysToShow: [
        'owner',
        'subjectType',
        'miPymeExpectedWorkers',
        'approved',
        'endorsement',
        'createdAt',
      ],
    }),
  },
  {
    key: 'owner',
    title: 'proposalRequest.columns.owner',
    dataIndex: 'owner',
    type: 'user',
    responsive: ['lg'],
  },
  {
    key: 'subjectType',
    title: 'proposalRequest.columns.type',
    ellipsis: true,
    dataIndex: 'subjectType',
    sorteable: true,
    responsive: ['lg'],
    render: renderProposalRequestType
  },
  {
    key: 'status',
    title: 'status',
    align: 'center',
    dataIndex: 'status',
    sorteable: true,
    render: renderProposalRequestStatus,
    responsive: ['lg']
  },
  {
    key: 'comments',
    title: 'proposalRequest.columns.newComments',
    align: 'center',
    dataIndex: 'comments',
    sorteable: true,
    render: (value) => value || '-',
    responsive: ['lg']
  },
  {
    key: 'assigned',
    title: 'proposalRequest.columns.assigned',
    dataIndex: 'assigned',
    sorteable: true,
    ellipsis: true,
    responsive: ['lg'],
    align: 'center',
    render: renderProposalUserAssignedCell
  },
  {
    key: 'province',
    title: 'province',
    dataIndex: ['address', 'province'],
    responsive: ['lg'],
    align: 'center',
    ellipsis: true,
    width: 150,
    render: renderProvince
  },
  {
    key: 'municipality',
    title: 'municipality',
    dataIndex: ['address', 'municipality'],
    responsive: ['lg'],
    align: 'center',
    width: 150,
    ellipsis: true,
    render: renderMunicipality
  },
  {
    key: 'miPymeExpectedWorkers',
    title: 'proposalRequest.columns.workers',
    align: 'center',
    sorteable: true,
    ellipsis: true,
    dataIndex: 'miPymeExpectedWorkers',
    render: (value) => value || '-',
    responsive: ['lg']
  },
  {
    key: 'partners',
    title: 'proposalRequest.columns.partners',
    align: 'center',
    dataIndex: 'numberOfPartners',
    ellipsis: true,
    render: (value) => value || 0,
    responsive: ['lg']
  },
  {
    key: 'capital',
    title: 'proposalRequest.columns.capital',
    align: 'center',
    ellipsis: true,
    dataIndex: 'socialCapital',
    render: (value) => value ? showPrice(value) : '-',
    responsive: ['lg']
  },
  {
    key: 'createdAt',
    title: 'createdAt',
    type: 'date',
    sorteable: true,
    align: 'center',
    dataIndex: 'createdAt',
    width: 150,
    responsive: ['lg']
  },
  {
    key: 'timePass',
    title: 'proposalRequest.columns.timePass',
    ellipsis: true,
    dataIndex: 'timePass',
    align: 'center',
    responsive: ['lg'],
    render: (_, record) => renderBusinessDays(record?.metadata?.sentAt)
  },
  {
    key: 'certificationAval',
    title: 'proposalRequest.columns.certificationAval',
    ellipsis: true,
    dataIndex: 'certificationAval',
    align: 'center',
    responsive: ['lg'],
    // eslint-disable-next-line react/display-name
    render: (value) => <BooleanPreview value={!!value}/>
  },
  {
    key: 'lastSentAt',
    title: 'proposalRequest.columns.lastSentAt',
    ellipsis: true,
    sorteable: false,
    type: 'date',
    dataIndex: ['metadata', 'sentAt'],
    align: 'center',
    responsive: ['lg']
  },
  {
    key: 'validatedAt',
    title: 'proposalRequest.columns.validatedAt',
    ellipsis: true,
    sorteable: false,
    type: 'date',
    dataIndex: ['metadata', 'validatedAt'],
    align: 'center',
    responsive: ['lg']
  },
  {
    key: 'timePassFromValidated',
    title: 'proposalRequest.columns.timePassFromValidated',
    ellipsis: true,
    dataIndex: 'timePassFromValidated',
    align: 'center',
    responsive: ['lg'],
    render: (_, record) => {
      const value = record?.metadata?.validatedAt;
      if (record?.status === VALIDATED) {
        return renderBusinessDays(value || record?.updatedAt);
      } else if (record?.status === ACCEPTED) {
        return renderBusinessDays(record?.metadata?.acceptedAt || record?.updatedAt);
      } else {
        return renderBusinessDays(record?.createdAt || record?.updatedAt);
      }
    }
  },
  {
    key: 'actions',
    title: 'actions',
    dataIndex: 'actions',
    align: 'center',
    width: 100,
    render: renderProposalRequestActions
  }
];
